import _ from 'lodash';
import { StrikeUtility } from 'v2/common/components/StrikeBasedSelector/utility';
import { PipeDefinitionUtility } from 'v2/common/quantConfig/index';
import { pipeInputSchemaTypes } from 'v2/common/quantConfig/pipeDefinitions/config';
import { extractIdsFromLabel, extractIndividualSchemaField, extractCaseBasedSchemaField }
  from '../../utils/signal';

export const createLabelMapping = (cases, shouldResetLabels) => {
  const allIntents = _.flatMap(cases, ({ entry: { intents }, adjustments }) =>
    _.concat(intents, _.flatMap(adjustments, 'intents'))
  );

  const incrementalLabel = {};
  const labelMappedConfig = {};

  allIntents.forEach(({ identifier: { label, caseIndex } }) => {
    if (!label) return;

    labelMappedConfig[caseIndex] = labelMappedConfig[caseIndex] || {};

    if (shouldResetLabels) {
      incrementalLabel[caseIndex] = (incrementalLabel[caseIndex] || 0) + 1;
      const { basketId } = extractIdsFromLabel(label);
      labelMappedConfig[caseIndex][label] = basketId
        ? `${incrementalLabel[caseIndex]}(${basketId})`
        : `${incrementalLabel[caseIndex]}`;
    } else {
      const { basketId, intentId } = extractIdsFromLabel(label);
      labelMappedConfig[caseIndex][label] = basketId
        ? `${labelMappedConfig[caseIndex][intentId]}(${basketId})`
        : labelMappedConfig[caseIndex][label] || label;
    }
  });

  return labelMappedConfig;
};

export const getLastLabelByCaseIndex = (caseIndexMapping, labelMappedConfig) => {
  return _.reduce(
    labelMappedConfig,
    (acc, labels, caseIndex) => {
      const mappedIndex = caseIndexMapping[caseIndex];
      if (mappedIndex !== undefined) {
        const numericLabels = _.map(labels, Number);
        acc[mappedIndex] = _.max(numericLabels) || 0;
      }
      return acc;
    },
    {}
  );
};

export const updateOperand = (operand, labelMappedConfig, caseIndexMapping) => {
  if (!_.startsWith(operand, "case.")) return operand;
  const {
    caseIndex, label, intentId, field, basketId
  } = extractIndividualSchemaField(operand || '');
  if (_.isEmpty(label)) {
    const { caseIndex, field } = extractCaseBasedSchemaField(operand);
    return ['case', caseIndexMapping[caseIndex], field].join('.');
  }

  const updatedLabel = basketId
    ? `${labelMappedConfig[caseIndex][intentId]}(${basketId})`
    : labelMappedConfig[caseIndex][label];

  return ['case', caseIndexMapping[caseIndex], 'leg', updatedLabel, field].join('.');
};

export const updateStrike = (strike, labelMappedConfig, caseIndexMapping) => {
  const newStrike = _.cloneDeep(strike);
  newStrike.valuePath = updateOperand(newStrike.valuePath, labelMappedConfig, caseIndexMapping);
  return newStrike;
};

export const updateSignals = (signals, labelMappedConfig, caseIndexMapping) => _.map(signals, (signal) => ({
  ..._.cloneDeep(signal),
  leftOperand: updateOperand(signal.leftOperand, labelMappedConfig, caseIndexMapping),
  rightOperand: updateOperand(signal.rightOperand, labelMappedConfig, caseIndexMapping),
}));

export const updateIntents = (intents, labelMappedConfig, caseIndexMapping) => intents.map((intent) => {
  const newIntent = _.cloneDeep(intent);
  const existingCaseIndex = newIntent.identifier.caseIndex;

  newIntent.identifier.label = labelMappedConfig[existingCaseIndex][intent.identifier.label];
  newIntent.identifier.caseIndex = caseIndexMapping[existingCaseIndex];

  if (intent.uiConfigs?.quickAdjustments) {
    newIntent.uiConfigs.quickAdjustments = _.map(intent.uiConfigs.quickAdjustments, (quickAdjustment) => ({
      ...quickAdjustment,
      targets: _.map(
        quickAdjustment.targets,
        (target) => labelMappedConfig[existingCaseIndex][target] || target
      ),
    }));
  }

  if (intent.instrument?.strike) {
    const { isIndicatorBased } = StrikeUtility.getStrikeObj(intent.instrument.strike);
    if (isIndicatorBased) {
      newIntent.instrument.strike = updateStrike(
        intent.instrument.strike, labelMappedConfig, caseIndexMapping
      );
    }
  }

  if (newIntent.target) {
    const { basketId, intentId } = extractIdsFromLabel(newIntent.target.label || '');
    const updatedIntentId = basketId
      ? `${labelMappedConfig[existingCaseIndex][intentId]}(${basketId})`
      : labelMappedConfig[existingCaseIndex][intentId];

    newIntent.target.label = updatedIntentId || newIntent.target.label;
    newIntent.target.caseIndex = caseIndexMapping[existingCaseIndex];
  }

  if (newIntent.instrument?.type === 'capture') {
    newIntent.instrument.when = updateSignals(newIntent.instrument.when, labelMappedConfig, caseIndexMapping);
  }

  return newIntent;
});

export const createCaseIndexMapping = (cases, deletedIndex = undefined) => cases.reduce((acc, __, index) => {
  acc[index] = (!_.isUndefined(deletedIndex) && index > deletedIndex) ? index - 1 : index;
  return acc;
}, {});

export const updateCases = (cases, labelMappedConfig, caseIndexMapping) => cases.map((caseConfig) => ({
  ...caseConfig,
  entry: {
    ...caseConfig.entry,
    intents: updateIntents(caseConfig.entry.intents, labelMappedConfig, caseIndexMapping),
    signal: updateSignals(caseConfig.entry.signal, labelMappedConfig, caseIndexMapping),
  },
  exitSignal: updateSignals(caseConfig.exitSignal, labelMappedConfig, caseIndexMapping),
  adjustments: caseConfig.adjustments.map((adjustment) => ({
    ...adjustment,
    intents: updateIntents(adjustment.intents, labelMappedConfig, caseIndexMapping),
    signal: updateSignals(adjustment.signal, labelMappedConfig, caseIndexMapping),
  })),
}));

export const updatePipeConfigs = (pipeConfigs, labelMappedConfig, caseIndexMapping) => {
  return _.map(pipeConfigs, (pipeConfig) => {
    const inputSchema = PipeDefinitionUtility.getPipeInputSchemaByType(pipeConfig.type);
    const signalSchema = _.find(inputSchema, ({ type }) => type === pipeInputSchemaTypes.signal);
    if (_.isEmpty(signalSchema)) return pipeConfig;

    return {
      ...pipeConfig,
      config: {
        ...pipeConfig.config,
        [signalSchema.name]: updateSignals(
          _.get(pipeConfig.config, signalSchema.name), labelMappedConfig, caseIndexMapping
        ),
      },
    };
  });
};
